import { Flex, Modal, Radio, RadioGroup, Switch, Typography } from 'components'
import { useMapContext } from 'components/MapV2/MapProvider'
import React, { type PropsWithChildren } from 'react'
import type { MapSettingsField, MapSettingsFieldGroup, OnMapSettingsChange } from './types'
import { useTranslation } from 'utils/useTranslation'

export type MapSettingsV2Props = {
  isOpen: boolean
  onClose: () => void
}

export const MapSettingsV2: React.FC<MapSettingsV2Props> = ({ isOpen, onClose }) => {
  const { t } = useTranslation('components.map.settings')
  const { t: tGlobal } = useTranslation()
  const { settingsV2 } = useMapContext()

  if (!settingsV2) return null

  const { fields, onChange, values } = settingsV2

  const toggleFields = fields.filter((field) => field.type === 'toggle')
  const radioFields = fields.filter((field) => field.type === 'radio')

  return (
    <Modal
      isOpen={isOpen}
      onSubmit={onClose}
      noCancel
      onRequestClose={onClose}
      heading={t('title')}
      customSubmitText={tGlobal('okay')}
    >
      <Flex directionColumn="zero" gap={1}>
        {radioFields.map((field, i) => (
          <MapSettingGroup title={field.title} key={i}>
            <MapSettingRadio
              fields={field.fields}
              onChange={onChange}
              values={values[field.key] ?? {}}
              groupKey={field.key}
            />
          </MapSettingGroup>
        ))}
        {toggleFields.map((field, i) => (
          <MapSettingGroup title={field.title} key={i}>
            {field.fields.map((toggleField, i) => (
              <MapSettingToggle
                groupKey={field.key}
                key={i}
                field={toggleField}
                onChange={onChange}
                value={!!values?.[field.key]?.[toggleField.id]}
              />
            ))}
          </MapSettingGroup>
        ))}
      </Flex>
    </Modal>
  )
}

const MapSettingGroup: React.FC<PropsWithChildren<Pick<MapSettingsFieldGroup, 'title'>>> = ({
  title,
  children,
}) => (
  <Flex directionColumn="zero" gap={1}>
    <Typography fontWeight="semibold">{title}</Typography>
    {children}
  </Flex>
)

const MapSettingToggle: React.FC<{
  field: MapSettingsField
  groupKey: string
  value: boolean
  onChange: OnMapSettingsChange
}> = ({ field, value, onChange, groupKey }) => {
  return (
    <Flex between="zero" middle="zero">
      <Typography>{field.label}</Typography>
      <Switch
        componentSize="small"
        disabled={field.hidable === false}
        checked={value}
        onChange={(e) => {
          const value = e.target.checked
          onChange(groupKey, field.id, value)
        }}
      />
    </Flex>
  )
}

const MapSettingRadio: React.FC<{
  fields: Array<MapSettingsField>
  groupKey: string
  values: Record<string, boolean>
  onChange: OnMapSettingsChange
}> = ({ fields, onChange, values, groupKey }) => {
  const keys = fields.map((field) => field.id)
  const onChangeHandler = (key: string) => {
    const otherKeys = fields.filter((field) => field.id !== key).map((field) => field.id)
    onChange(groupKey, key, true)
    otherKeys.forEach((key) => onChange(groupKey, key, false))
  }

  const selected =
    Object.keys(values).find((key) => keys.includes(key) && values[key]) ?? fields[0].id

  return (
    <RadioGroup value={selected} onChange={(e) => onChangeHandler(e.target.value)}>
      {fields.map((field, i) => (
        <Radio key={i} value={field.id} label={field.label} />
      ))}
    </RadioGroup>
  )
}
